/**
 * LiveMonitor Component
 *
 * Real-time monitoring panel showing:
 * - Active discovery tasks with per-suburb progress
 * - Active scraping runs
 * - Live log stream
 */

import { useEffect, useRef } from "react";
import {
  Activity,
  Bot,
  Loader2,
  AlertCircle,
  Info,
  AlertTriangle,
  Bug,
  RefreshCw,
  ArrowDown,
  MapPin,
  Search,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@repo/ui/card";
import { Progress } from "@repo/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/select";
import { ScrollArea } from "@repo/ui/scroll-area";
import { cn } from "@repo/ui/lib/utils";
import { useLiveMonitor } from "@/hooks/useQueueRealtime";

const logLevelConfig = {
  info: { icon: Info, className: "text-blue-500", label: "INFO" },
  warn: { icon: AlertTriangle, className: "text-amber-500", label: "WARN" },
  error: { icon: AlertCircle, className: "text-red-500", label: "ERROR" },
  debug: { icon: Bug, className: "text-gray-400", label: "DEBUG" },
};

function formatTimestamp(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

function formatDuration(startedAt: string): string {
  const start = new Date(startedAt);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - start.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

export function LiveMonitor() {
  const {
    logs,
    activeRuns,
    discoveryTasks,
    discoveryStats,
    isLoading,
    isLoadingMore,
    hasMoreLogs,
    logLevel,
    setLogLevel,
    autoScroll,
    setAutoScroll,
    refetch,
    loadMoreLogs,
  } = useLiveMonitor();

  const logContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoScroll && logContainerRef.current) {
      logContainerRef.current.scrollTop = 0;
    }
  }, [autoScroll]);

  return (
    <div className="space-y-4">
      {/* Discovery Progress Section */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="w-5 h-5" />
              Discovery Tasks
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={refetch}
              type="button"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Stats bar */}
          {discoveryStats.total > 0 && (
            <div className="flex gap-3 mb-3 text-xs">
              <span className="text-muted-foreground">Tasks: <strong className="text-foreground">{discoveryStats.total}</strong></span>
              <span className="text-yellow-600">Pending: <strong>{discoveryStats.pending}</strong></span>
              <span className="text-blue-600">In Progress: <strong>{discoveryStats.inProgress}</strong></span>
              <span className="text-green-600">Done: <strong>{discoveryStats.completed}</strong></span>
              {discoveryStats.failed > 0 && <span className="text-red-600">Failed: <strong>{discoveryStats.failed}</strong></span>}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : discoveryTasks.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Search className="w-8 h-8 mx-auto opacity-30 mb-2" />
              <p className="text-sm">No active discovery tasks</p>
              <p className="text-xs mt-1">Start a job to begin URL discovery</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[300px]">
              <div className="space-y-1.5">
                {discoveryTasks.map((task) => {
                  const isActive = task.status === 'in_progress' && task.locked_by;
                  const isDone = task.status === 'completed';
                  const isFailed = task.status === 'failed';

                  return (
                    <div
                      key={task.id}
                      className={cn(
                        "flex items-center gap-2 px-2 py-1.5 rounded text-xs",
                        isActive && "bg-blue-50 dark:bg-blue-950/20",
                        isDone && "bg-green-50/50 dark:bg-green-950/10 opacity-60",
                        isFailed && "bg-red-50/50 dark:bg-red-950/10"
                      )}
                    >
                      {/* Status indicator */}
                      <div className={cn(
                        "w-2 h-2 rounded-full flex-shrink-0",
                        isActive && "bg-blue-500 animate-pulse",
                        isDone && "bg-green-500",
                        isFailed && "bg-red-500",
                        task.status === 'pending' && "bg-yellow-400"
                      )} />

                      {/* Suburb name */}
                      <div className="w-[120px] flex-shrink-0 truncate">
                        <MapPin className="w-3 h-3 inline mr-1 text-muted-foreground" />
                        <span className="font-medium">
                          {task.suburbs?.suburb_name || task.suburb_id}
                        </span>
                        <span className="text-muted-foreground ml-1">
                          {task.suburbs?.state}
                        </span>
                      </div>

                      {/* Progress */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">p{task.current_page}</span>
                          <span className="text-foreground font-medium">{task.urls_found} URLs</span>
                          {isActive && (
                            <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                              working
                            </Badge>
                          )}
                          {isDone && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                          {isFailed && task.error_message && (
                            <span className="text-red-500 truncate">{task.error_message}</span>
                          )}
                        </div>
                      </div>

                      {/* Adapter */}
                      <Badge variant="outline" className="text-[10px] flex-shrink-0">
                        {task.adapters?.slug || 'unknown'}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Active Job Runs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bot className="w-5 h-5" />
              Active Jobs
            </CardTitle>
            <Badge variant="outline" className="gap-1">
              <Activity className="w-3 h-3" />
              {activeRuns.length} running
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {activeRuns.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground text-sm">
              No active jobs
            </div>
          ) : (
            <div className="space-y-3">
              {activeRuns.map((run) => {
                const discovered = run.urls_discovered || 0;
                const scraped = run.urls_scraped || 0;
                const progress = discovered > 0 ? Math.round((scraped / discovered) * 100) : 0;
                const phase = run.phase || 'running';

                return (
                  <div key={run.id} className="border rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                        <span className="font-medium text-sm">
                          {run.scrape_jobs?.name || "Unknown Job"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs capitalize">{phase}</Badge>
                        <span className="text-xs text-muted-foreground">{formatDuration(run.started_at)}</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>
                          {phase === 'discovery' ? `Discovering URLs... (${discovered} found)` : `Scraping: ${scraped} / ${discovered} URLs`}
                        </span>
                        <span>{progress}%</span>
                      </div>
                      <Progress value={progress} className="h-1.5" />
                    </div>
                    <div className="flex gap-4 text-xs text-muted-foreground">
                      <span>Discovered: <strong className="text-foreground">{discovered}</strong></span>
                      <span>Scraped: <strong className="text-foreground">{scraped}</strong></span>
                      <span>Records: <strong className="text-foreground">{run.records_scraped || 0}</strong></span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live Log Stream */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Live Logs
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={logLevel} onValueChange={setLogLevel}>
                <SelectTrigger className="w-[100px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warn">Warning</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                  <SelectItem value="debug">Debug</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="ghost" size="sm" className={cn("h-8 w-8 p-0", autoScroll && "text-accent")} onClick={() => setAutoScroll(!autoScroll)} type="button">
                <ArrowDown className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={refetch} type="button">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div ref={logContainerRef} className="max-h-[500px] overflow-y-auto font-mono text-xs">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Activity className="w-8 h-8 mx-auto opacity-30 mb-2" />
                <p className="text-sm">No logs yet</p>
                <p className="text-xs mt-1">Logs appear here when crawl operations run</p>
              </div>
            ) : (
              <>
                <div className="divide-y divide-border/50">
                  {logs.map((log) => {
                    const config = logLevelConfig[log.level] || logLevelConfig.info;
                    const LevelIcon = config.icon;
                    return (
                      <div key={log.id} className={cn(
                        "flex items-start gap-2 px-4 py-1.5 hover:bg-muted/50",
                        log.level === 'error' && "bg-red-50/50 dark:bg-red-950/10",
                        log.level === 'warn' && "bg-amber-50/30 dark:bg-amber-950/10"
                      )}>
                        <span className="text-muted-foreground flex-shrink-0 w-[60px] text-right">{formatTimestamp(log.created_at)}</span>
                        <LevelIcon className={cn("w-3.5 h-3.5 flex-shrink-0 mt-0.5", config.className)} />
                        <span className={cn("flex-shrink-0 w-[40px] font-semibold", config.className)}>{config.label}</span>
                        {log.scrape_jobs?.name && (
                          <span className="text-muted-foreground flex-shrink-0">[{log.scrape_jobs.name}]</span>
                        )}
                        <span className="text-foreground break-all">{log.message}</span>
                      </div>
                    );
                  })}
                </div>
                {hasMoreLogs && (
                  <div className="flex justify-center py-3 border-t border-border/50">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={loadMoreLogs}
                      disabled={isLoadingMore}
                      className="text-xs gap-1"
                      type="button"
                    >
                      {isLoadingMore ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />}
                      Load Older Logs
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
